/* src/pages/Pipeline.jsx
 * Workspace Pipeline kanban — conversations grouped by dealStage.
 * Data: getWorkspaceConversations. Moves: leadApi.updateDealStage (same as Inbox).
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { useToast } from '../contexts/ToastContext';
import { getUserWorkspaceAccess } from '../utils/accessControl';
import { leadApi } from '../utils/api';
import {
  DEAL_STAGES,
  dealStageLabel,
  LeadRowChips,
  ConversionChips,
} from '../components/inbox/chips';
import {
  computePipelineSnapshot,
  getSortTime,
  leadDisplayName,
  formatMoney,
} from '../utils/todayStats';
import { PipelineBoardSkeleton } from '../components/Skeleton';

function PipelineCard({
  conv,
  isMobile,
  isMoving,
  isDragging,
  moveMenuOpen,
  onToggleMoveMenu,
  onMove,
  onDragStart,
  onDragEnd,
  onCardClick,
}) {
  const stageId = conv.dealStage || 'new';

  return (
    <div
      className={`pipeline-card${isDragging ? ' is-dragging' : ''}`}
      draggable={!isMoving && !isMobile}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCardClick();
        }
      }}
    >
      <div className="pipeline-card-top">
        <div className="pipeline-card-identity">
          <div className="pipeline-card-name">{leadDisplayName(conv)}</div>
          {conv.leadEmail && (
            <div className="pipeline-card-email mono">{conv.leadEmail}</div>
          )}
        </div>
        <div style={{ position: 'relative', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="inbox-pill-btn pipeline-move-btn"
            disabled={isMoving}
            onClick={() => onToggleMoveMenu(conv.id)}
          >
            Move
            <span className="mono" style={{ fontSize: '9px', color: 'var(--dim)' }}>{'\u25be'}</span>
          </button>
          {moveMenuOpen && (
            <div className="pipeline-move-menu">
              {DEAL_STAGES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  disabled={isMoving}
                  onClick={() => onMove(conv, s.id)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    textAlign: 'left',
                    background: s.id === stageId ? 'color-mix(in srgb, var(--accent) 28%, transparent)' : 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontFamily: "'Archivo', sans-serif",
                    color: 'var(--ink)',
                    fontWeight: s.id === stageId ? 600 : 400,
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {formatMoney(conv.dealValue) && (
        <div className="pipeline-card-value">{formatMoney(conv.dealValue)}</div>
      )}

      <LeadRowChips lead={conv} />

      {stageId === 'won' && (
        <div style={{ marginTop: '8px' }}>
          <ConversionChips conversionUploads={conv.conversionUploads} />
        </div>
      )}
    </div>
  );
}

export default function Pipeline() {
  const { currentUser } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [movingId, setMovingId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dropTargetStage, setDropTargetStage] = useState(null);
  const [openMoveMenuId, setOpenMoveMenuId] = useState(null);
  const [activeStage, setActiveStage] = useState('new');
  const didDragRef = useRef(false);

  useEffect(() => {
    if (!currentUser || !selectedWorkspaceId) {
      setConversations([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setAccessDenied(false);

    async function load() {
      try {
        const access = await getUserWorkspaceAccess(currentUser.uid, selectedWorkspaceId);
        if (cancelled) return;
        if (!access.leadAccess && !access.isOwner) {
          setAccessDenied(true);
          setConversations([]);
          setLoading(false);
          return;
        }

        const result = await leadApi.getWorkspaceConversations(selectedWorkspaceId);
        if (cancelled) return;
        setConversations(result.conversations || []);
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setConversations([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [currentUser, selectedWorkspaceId]);

  const columnMeta = useMemo(() => computePipelineSnapshot(conversations), [conversations]);

  const columns = useMemo(() => {
    const byStage = {};
    DEAL_STAGES.forEach((s) => { byStage[s.id] = []; });

    conversations.forEach((conv) => {
      const stage = conv.dealStage || 'new';
      const bucket = byStage[stage] || byStage.new;
      bucket.push(conv);
    });

    DEAL_STAGES.forEach((s) => {
      byStage[s.id].sort((a, b) => getSortTime(b) - getSortTime(a));
    });

    return DEAL_STAGES.map((s) => ({
      ...s,
      cards: byStage[s.id],
      meta: columnMeta.find((m) => m.id === s.id) || { count: 0, value: 0 },
    }));
  }, [conversations, columnMeta]);

  async function handleMove(conv, newStage) {
    const currentStage = conv.dealStage || 'new';
    if (newStage === currentStage) {
      setOpenMoveMenuId(null);
      return;
    }

    const prevStage = currentStage;
    const dealValue = conv.dealValue ?? null;

    setMovingId(conv.id);
    setOpenMoveMenuId(null);
    setConversations((prev) =>
      prev.map((c) => (c.id === conv.id ? { ...c, dealStage: newStage } : c))
    );

    try {
      const result = await leadApi.updateDealStage(conv.id, newStage, dealValue);
      if (result.success) {
        showToast('Deal stage updated');
      } else {
        setConversations((prev) =>
          prev.map((c) => (c.id === conv.id ? { ...c, dealStage: prevStage } : c))
        );
        showToast(result.error || 'Failed to update deal stage', 'error');
      }
    } catch {
      setConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, dealStage: prevStage } : c))
      );
      showToast('Failed to update deal stage', 'error');
    } finally {
      setMovingId(null);
    }
  }

  function handleCardClick(conv) {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    const path = conv.formId
      ? `/leads/${conv.formId}?conversation=${conv.id}`
      : `/leads?conversation=${conv.id}`;
    navigate(path);
  }

  function handleDragStart(e, convId) {
    didDragRef.current = false;
    setDraggingId(convId);
    e.dataTransfer.setData('text/plain', convId);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragEnd() {
    didDragRef.current = true;
    setDraggingId(null);
    setDropTargetStage(null);
  }

  function handleDrop(e, stageId) {
    e.preventDefault();
    const convId = e.dataTransfer.getData('text/plain');
    const conv = conversations.find((c) => c.id === convId);
    setDropTargetStage(null);
    if (conv) handleMove(conv, stageId);
  }

  if (loading) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
        <div className="today-page" style={{ padding: isMobile ? '1rem' : '24px 32px 48px' }}>
          <h1 className="today-h1" style={{ marginBottom: '1.5rem' }}>Pipeline</h1>
          <PipelineBoardSkeleton />
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
        <div className="today-page" style={{ padding: isMobile ? '1rem' : '24px 32px 48px' }}>
          <div className="today-access-card">
            <h2 style={{ marginTop: 0, color: 'var(--ink)' }}>Access denied</h2>
            <p style={{ color: 'var(--muted)', marginBottom: 0 }}>
              You do not have lead access for this workspace. Ask the owner to grant lead permissions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)', minWidth: 0 }}>
      <div className="today-page" style={{ padding: isMobile ? '1rem 1rem 2rem' : '24px 32px 48px' }}>
        <div className="today-header">
          <div className="today-header-left">
            <h1 className="today-h1">Pipeline</h1>
          </div>
        </div>

        {loadError && (
          <div className="today-load-error">Could not load deals. Refresh to retry.</div>
        )}

        {isMobile && (
          <div className="pipeline-mobile-tabs fb-tab-bar">
            <div className="fb-tab-bar-inner is-mobile">
              {columns.map((col) => (
                <button
                  key={col.id}
                  type="button"
                  className={`fb-tab-btn is-mobile${activeStage === col.id ? ' active' : ''}`}
                  onClick={() => setActiveStage(col.id)}
                >
                  {dealStageLabel(col.id)}
                  <span className="pipeline-mobile-tab-count">{col.meta.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={`pipeline-board${isMobile ? ' is-mobile-tabs' : ''}`}>
          {(isMobile ? columns.filter((col) => col.id === activeStage) : columns).map((col) => {
            const valueLabel = formatMoney(col.meta.value);
            const isLost = col.id === 'lost';
            const isWon = col.id === 'won';
            const isDropTarget = dropTargetStage === col.id;

            return (
              <div
                key={col.id}
                className={`pipeline-column${isLost ? ' lost' : ''}${isWon ? ' won' : ''}${isDropTarget ? ' drop-target' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropTargetStage(col.id);
                }}
                onDragLeave={() => setDropTargetStage((prev) => (prev === col.id ? null : prev))}
                onDrop={(e) => handleDrop(e, col.id)}
              >
                <div className={`pipeline-column-header${isWon ? ' tinted' : ''}`}>
                  <div className={`pipeline-column-label${isWon ? ' accent' : ''}`}>
                    {dealStageLabel(col.id).toUpperCase()}
                  </div>
                  <div className="pipeline-column-stats">
                    <span className="pipeline-column-count">{col.meta.count}</span>
                    {valueLabel && (
                      <span className={`pipeline-column-value${isWon ? ' accent' : ''}`}>{valueLabel}</span>
                    )}
                  </div>
                </div>

                <div className="pipeline-column-body">
                  {col.cards.length === 0 ? (
                    <div className="today-empty">No deals here</div>
                  ) : (
                    col.cards.map((conv) => (
                      <PipelineCard
                        key={conv.id}
                        conv={conv}
                        isMobile={isMobile}
                        isMoving={movingId === conv.id}
                        isDragging={draggingId === conv.id}
                        moveMenuOpen={openMoveMenuId === conv.id}
                        onToggleMoveMenu={(id) => setOpenMoveMenuId((prev) => (prev === id ? null : id))}
                        onMove={handleMove}
                        onDragStart={(e) => handleDragStart(e, conv.id)}
                        onDragEnd={handleDragEnd}
                        onCardClick={() => handleCardClick(conv)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
